import { createFileRoute } from '@tanstack/react-router';

import { buildGuards, hasFlagEnabled } from '@mint-lib/routing-guards';
import { Platforms } from '@mint-sdk/api-bff';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';
import { Metadata } from '@mint-sdk/api-mdm';
import { z } from 'zod';

const searchSchema = z.object({
  tab: z
    .union([
      z.literal('imports'),
      z.literal('templates'),
      z.literal('glossary'),
    ])
    .optional()
    .prefault('imports')
    .prefault('imports'),
  search: z.string().prefault(''),
  pageNumber: z.number().optional(),
  itemsPerPage: z.number().prefault(50),
  sortBy: z.string().optional(),
  sortDirection: z.union([z.literal('asc'), z.literal('desc')]).optional(),
  filters: z
    .object({
      // Common filters
      dateFrom: z.string().optional(),
      dateTo: z.string().optional(),
      campaign: z.string().array().optional(),
      activity: z.string().array().optional(),
      platform: z.string().array().optional(),
      // Past imports filters
      importType: z
        .array(z.union([z.literal('auto'), z.literal('manual')]))
        .optional(),
      status: z.array(DataIngestion.DataFile.DataFileStatusSchema).optional(),
      template: z.string().array().optional(),
      sender: z.string().array().optional(),
      includeHidden: z.boolean().optional(),
      // Templates filters
      email: z.string().array().optional(),
      granularityLevel: z
        .array(
          z.union([
            z.literal('pricing_item'),
            z.literal('platform'),
            z.literal('media_row'),
          ]),
        )
        .optional(),
      owner: z.string().array().optional(),
    })
    .optional(),
});

const importHubV2Guard = buildGuards(hasFlagEnabled('importHubV2'));

export const Route = createFileRoute(
  '/_secured/external-data/import-hub/performance/',
)({
  staleTime: 30 * 1000,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ cause, context, deps }) => {
    if (cause === 'stay') {
      return;
    }

    const armGuardsContext = await context.getGuardContext(
      await context.getInstance('flagsmith', '@mint/arm'),
    );
    const queryClient =
      await context.serviceLocator.getOrThrowInstance('queryClient');

    const params = {};

    const isImportHubV2 = importHubV2Guard(armGuardsContext);

    if (isImportHubV2) {
      const [, profile] = await context.serviceLocator.getInstance('profile');
      const companyId = profile?.getProfileData()?.profile.company.id;
      if (companyId) {
        queryClient.prefetchQuery(
          Metadata.getContextMetadataQueryOptions({
            params: {
              fieldUrnIds: ['urn:mint:field:platform_v2'],
              context: 'import_hub',
              companyId,
            },
          }),
        );
      }
    } else {
      queryClient.prefetchQuery(
        Platforms.listQueryOptions({
          params: { excludeDeleted: true, excludeNotIntegrated: false },
        }),
      );
    }

    if (deps.search.tab === 'imports') {
      if (isImportHubV2) {
        queryClient.prefetchQuery(
          DataIngestion.DataFile.listDataFilesV2QueryOptions({
            params,
          }),
        );
      } else {
        queryClient.prefetchQuery(
          DataIngestion.DataFile.listDataFilesV1QueryOptions({
            params,
          }),
        );
      }
    }

    if (deps.search.tab === 'templates') {
      if (isImportHubV2) {
        queryClient.prefetchQuery(
          DataIngestion.Templates.getTemplatesListV2QueryOptions({
            params,
          }),
        );
      } else {
        queryClient.prefetchQuery(
          DataIngestion.Templates.getTemplatesListV1QueryOptions({
            params,
          }),
        );
      }
    }
    if (deps.search.tab === 'glossary') {
      if (isImportHubV2) {
        queryClient.prefetchQuery(
          DataIngestion.Glossaries.getGlossaryListV2QueryOptions({
            params: {
              glossaryType: 'urn:mint:field:platform_v2',
              itemsPerPage: 10000,
            },
          }),
        );
        queryClient.prefetchQuery(
          DataIngestion.Glossaries.getGlossaryListV2QueryOptions({
            params: {
              glossaryType: 'urn:mint:field:channel_v2',
              itemsPerPage: 10000,
            },
          }),
        );
      } else {
        queryClient.prefetchQuery(
          DataIngestion.Glossaries.getGlossaryListQueryOptions({
            params: { glossaryType: 'platform', itemsPerPage: 10000 },
          }),
        );
        queryClient.prefetchQuery(
          DataIngestion.Glossaries.getGlossaryListQueryOptions({
            params: { glossaryType: 'channel', itemsPerPage: 10000 },
          }),
        );
      }
    }
  },
});
